import { useOktaAuth } from '@okta/okta-react';
import { useEffect } from 'react';
import type { FC } from 'react';
import type { Token } from '@okta/okta-auth-js';
import { AUTH_TOKEN_STORAGE_KEY, TOKEN_READY_EVENT } from '@constants/auth';

const publishToken = (token: string | undefined): void => {
	if (!token) return;

	sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
	window.dispatchEvent(new CustomEvent(TOKEN_READY_EVENT, { detail: { token } }));
};

const clearToken = (): void => {
	sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
	window.dispatchEvent(new CustomEvent(TOKEN_READY_EVENT, { detail: { token: '' } }));
};

const TokenBridge: FC = () => {
	const { oktaAuth } = useOktaAuth();

	useEffect(() => {
		// Publish whatever token already exists (covers an authenticated mount).
		publishToken(oktaAuth.getAccessToken());

		const onTokenChange = (key: string, token?: Token): void => {
			if (key === 'accessToken' && token && 'accessToken' in token) {
				publishToken(token.accessToken);
			}
		};

		const onTokenRemove = (key: string): void => {
			if (key === 'accessToken') clearToken();
		};

		// Track the token through its whole lifecycle. A background renewal does
		// NOT flip authState.isAuthenticated, so listening to the tokenManager is
		// the only way a refreshed token reaches the API layer — otherwise the
		// app keeps sending the original (eventually expired) token.
		oktaAuth.tokenManager.on('added', onTokenChange);
		oktaAuth.tokenManager.on('renewed', onTokenChange);
		oktaAuth.tokenManager.on('removed', onTokenRemove);
		oktaAuth.tokenManager.on('expired', onTokenRemove);

		return () => {
			oktaAuth.tokenManager.off('added', onTokenChange);
			oktaAuth.tokenManager.off('renewed', onTokenChange);
			oktaAuth.tokenManager.off('removed', onTokenRemove);
			oktaAuth.tokenManager.off('expired', onTokenRemove);
		};
	}, [oktaAuth]);

	return null;
};

export default TokenBridge;
